import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Tooltip,
  IconButton,
  Badge,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Switch,
  Popover,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import useGet from "hooks/useGet";
import useUpdate from "hooks/useUpdate";
import useCreate from 'hooks/useCreate';
import useDelete from "hooks/useDelete";
import { useSWRConfig } from 'swr';
import DynamicGemstoneCells from "./DynamicGemstoneCells";
import { triggerPriceUpdateAll, triggerPriceUpdateOne } from "utils/priceUpdateAll";
import API_BASE_URL from "config/api";
import apiClient from "utils/apiClient";
import { useAuth } from "pages/AuthProvider";

const uniqueStrings = (list = []) => [...new Set((Array.isArray(list) ? list : []).filter(Boolean))];
const pickId = (item) => (typeof item === "string" ? item : item?._id || item?.id || "");
const normalizeIdList = (list = []) =>
  uniqueStrings((Array.isArray(list) ? list : []).map(pickId).filter(Boolean));

const normalizeUploadReport = (response) => {
  if (!response || typeof response !== "object") return null;
  const diagnostics = Array.isArray(response.diagnosticInfo) ? response.diagnosticInfo : [];
  return {
    success: Boolean(response.success),
    parsed: Number(response.parsed || 0),
    inserted: Number(response.inserted || 0),
    skipped: Number(response.skipped || 0),
    failed: Number(response.failed || 0),
    message: response.message || "",
    failedSKUs: uniqueStrings(response.failedSKUs),
    missedSKUs: uniqueStrings(response.missedSKUs),
    skippedSKUs: uniqueStrings(response.skippedSKUs),
    diagnosticInfo: diagnostics.map((item, index) => ({
      id: `${item?.SKU || "unknown"}-${item?.row || index}`,
      sku: item?.SKU || "-",
      row: item?.row ?? "-",
      reason: item?.reason || item?.perRowError?.error || item?.preInsertValidationError?.error || "Unknown error",
    })),
  };
};

const resolveUploadedMediaUrl = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  const value = raw.trim().replace(/\\/g, "/");
  if (!value) return "";
  if (/^(https?:|blob:|data:)/i.test(value)) return value;
  const normalized = value.replace(/^\/+/, "");
  if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
  if (value.startsWith("/")) return `${API_BASE_URL}${value}`;
  if (normalized.startsWith("media/")) return `${API_BASE_URL}/uploads/${normalized}`;
  // Bare filenames (no path separator) are never valid server-saved paths.
  // The backend always returns /uploads/<folder>/<file> — handled above.
  // Anything reaching here is stale DB data (originalname, etc.) → ignore.
  return "";
};

const collectMediaUrls = (input, bucket = []) => {
  if (!input) return bucket;
  if (typeof input === "string") {
    const resolved = resolveUploadedMediaUrl(input);
    if (resolved) bucket.push(resolved);
    return bucket;
  }
  if (Array.isArray(input)) {
    input.forEach((item) => collectMediaUrls(item, bucket));
    return bucket;
  }
  if (typeof input === "object") {
    [
      input.url,
      input.path,
      input.location,
      input.filePath,
      input.filepath,
      input.secure_url,
      input.src,
    ].forEach((value) => {
      const resolved = resolveUploadedMediaUrl(value);
      if (resolved) bucket.push(resolved);
    });
    Object.values(input).forEach((value) => collectMediaUrls(value, bucket));
  }
  return bucket;
};

// Resolve a gallery images array (strings or objects) to full URLs.
// Each entry yields at most ONE URL — reads only the first valid known
// URL field from objects (no recursion), preventing duplicates.
const resolveGalleryItem = (item) => {
  if (!item) return "";
  if (typeof item === "string") return resolveUploadedMediaUrl(item);
  if (typeof item === "object") {
    const candidates = [item.url, item.path, item.location, item.filePath, item.filepath, item.secure_url, item.src];
    for (const val of candidates) {
      const resolved = resolveUploadedMediaUrl(val);
      if (resolved) return resolved;
    }
  }
  return "";
};
const resolveGalleryImages = (images) =>
  uniqueStrings((Array.isArray(images) ? images : []).map(resolveGalleryItem).filter(Boolean));

const normalizeMediaUploadUrls = (response) => uniqueStrings(collectMediaUrls(response));

const formatUploadError = (err) => {
  const payload = err?.response?.data;
  if (typeof payload === "string") return payload;
  if (payload?.message) return payload.message;
  if (payload?.error) return payload.error;
  return err?.message || "Upload failed";
};

const parseNullableNumber = (value, fallback = null) => {
  if (value === "" || value === null || typeof value === "undefined") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const revokeBlobUrl = (url) => {
  if (url && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

const revokeBlobUrls = (urls = []) => {
  (Array.isArray(urls) ? urls : []).forEach(revokeBlobUrl);
};

const formatReviewDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return String(value);
  }
};

const normalizeReviewReply = (reply) => {
  if (!reply || typeof reply !== "object") return null;
  const comment = String(reply.comment || "").trim();
  if (!comment) return null;
  return {
    ...reply,
    adminName: "ShirishSparkleGems",
    comment,
  };
};
const previewActionButtonSx = {
  position: "absolute",
  top: 6,
  right: 6,
  width: 24,
  height: 24,
  backgroundColor: "rgba(0, 0, 0, 0.65)",
  color: "#fff",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.8)",
  },
};

const PRECIOUS_TYPES = ["Ruby", "Sapphire", "Emerald", "Navratan", "Pearl"];
const SEMI_PRECIOUS_TYPES = [
  "Opal",
  "Garnet",
  "Topaz",
  "Amethyst",
  "Citrine",
  "Peridot",
  "Aquamarine",
  "Tourmaline",
  "Tanzanite",
  "Spinel",
  "Morganite",
  "Alexandrite",
  "Coral",
  "Moonstone",
  "Turquoise",
  "Jade",
  "Onyx",
  "LapisLazuli",
  "TigerEye",
  "Kunzite",
];
const toGemKey = (type = "") => type.toLowerCase().replace(/\s+/g, "");
const GEM_TYPES = ["Diamond", ...PRECIOUS_TYPES, ...SEMI_PRECIOUS_TYPES];
const GEM_KEY_TO_LABEL = GEM_TYPES.reduce((acc, type) => {
  acc[toGemKey(type)] = type;
  return acc;
}, {});
const listToCsv = (value) => (Array.isArray(value) ? value.join(", ") : "");
const formatDetailValue = (value) => {
  if (value === null || typeof value === "undefined" || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    return value.map((item) => formatDetailValue(item)).join(", ");
  }
  if (typeof value === "object") {
    return value.name || value.title || value.label || value.categoryName || value.subCategoryName || value._id || value.id || "-";
  }
  return String(value);
};
const csvToList = (value) =>
  typeof value === "string"
    ? value.split(",").map((item) => item.trim()).filter(Boolean)
    : [];
const getGalleryImages = (product) =>
  Array.isArray(product?.media?.galleryImages)
    ? product.media.galleryImages
    : Array.isArray(product?.galleryImages)
      ? product.galleryImages
      : typeof product?.galleryImages === "string"
        ? product.galleryImages
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        : [];
const toSearchText = (value) => {
  if (value === null || typeof value === "undefined") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map((item) => toSearchText(item)).join(" ");
  if (typeof value === "object") {
    return [
      value.name,
      value.title,
      value.label,
      value.categoryName,
      value.sku,
      value.SKU,
      value._id,
      value.id,
    ]
      .filter(Boolean)
      .join(" ");
  }
  return "";
};
const matchesProductSearch = (product = {}, query = "") => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  const haystack = [
    product?.title,
    product?.name,
    product?.sku,
    product?.SKU,
    product?.description,
    product?.stock,
    toSearchText(product?.category),
    toSearchText(product?.subcategories || product?.subCategories),
    toSearchText(product?.tags || product?.tag),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(normalizedQuery);
};
const toCell = (family, item = {}) => ({
  Type: item?.type || "",
  Weight: listToCsv(item?.weight),
  Size: listToCsv(item?.size),
  Shape: listToCsv(item?.shape),
  Quality: Array.isArray(item?.quality) ? item.quality[0] || "" : item?.quality || "",
  Pieces: listToCsv(item?.pieces),
  family,
});
const hasMeaningfulData = (cell = {}) => {
  // Check if cell has any data beyond just the Type field
  return !!(
    cell?.Weight ||
    cell?.Size ||
    cell?.Shape ||
    cell?.Quality ||
    cell?.Pieces
  );
};
const productToCells = (product = {}) => {
  const cells = {};
  if (Array.isArray(product?.diamond) && product.diamond.length > 0) {
    const filteredDiamonds = product.diamond
      .map((item) => toCell("Precious Gemstones", item))
      .filter(hasMeaningfulData);
    if (filteredDiamonds.length > 0) {
      cells.Diamond = filteredDiamonds;
    }
  }
  const precious = product?.gemstones?.preciousGemstones || {};
  Object.entries(precious).forEach(([key, items]) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const typeLabel = GEM_KEY_TO_LABEL[key] || key;
    const filteredItems = items
      .map((item) => toCell("Precious Gemstones", item))
      .filter(hasMeaningfulData);
    if (filteredItems.length > 0) {
      cells[typeLabel] = filteredItems;
    }
  });
  const semi = product?.gemstones?.semiPreciousGemstones || {};
  Object.entries(semi).forEach(([key, items]) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const typeLabel = GEM_KEY_TO_LABEL[key] || key;
    const filteredItems = items
      .map((item) => toCell("Semi-Precious Gemstones", item))
      .filter(hasMeaningfulData);
    if (filteredItems.length > 0) {
      cells[typeLabel] = filteredItems;
    }
  });
  return cells;
};
const cellsToGemPayload = (cells = {}) => {
  const diamond = [];
  const gemstones = {
    preciousGemstones: PRECIOUS_TYPES.reduce((acc, type) => {
      acc[toGemKey(type)] = [];
      return acc;
    }, {}),
    semiPreciousGemstones: SEMI_PRECIOUS_TYPES.reduce((acc, type) => {
      acc[toGemKey(type)] = [];
      return acc;
    }, {}),
  };
  Object.keys(cells || {}).forEach((type) => {
    const rows = Array.isArray(cells[type]) ? cells[type] : [];
    const typeKey = toGemKey(type);
    rows.forEach((item) => {
      const gem = {
        type: item?.Type || "",
        weight: csvToList(item?.Weight),
        size: csvToList(item?.Size),
        shape: csvToList(item?.Shape),
        pieces: csvToList(item?.Pieces),
      };
      if (type === "Diamond" && item?.Quality) gem.quality = [item.Quality];
      if (type === "Diamond") {
        diamond.push(gem);
      } else if (PRECIOUS_TYPES.includes(type)) {
        gemstones.preciousGemstones[typeKey].push(gem);
      } else if (SEMI_PRECIOUS_TYPES.includes(type)) {
        gemstones.semiPreciousGemstones[typeKey].push(gem);
      }
    });
  });
  return { diamond, gemstones };
};

const ProductList = () => {
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const [products, setProducts] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [viewProduct, setViewProduct] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewCells, setViewCells] = useState({});
  const [editProduct, setEditProduct] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [editCells, setEditCells] = useState({});
  const [editGemFamily, setEditGemFamily] = useState("");
  const [editHeroFile, setEditHeroFile] = useState(null);
  const [editGalleryFiles, setEditGalleryFiles] = useState([]);
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editHeroPreview, setEditHeroPreview] = useState("");
  const [editGalleryPreviews, setEditGalleryPreviews] = useState([]);
  const [editVideoPreview, setEditVideoPreview] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [productFilterAnchorEl, setProductFilterAnchorEl] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [publishedFilter, setPublishedFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadReport, setUploadReport] = useState(null);
  const [toggleSavingKey, setToggleSavingKey] = useState("");
  const [productReviews, setProductReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState("");
  const [reviewReplyDrafts, setReviewReplyDrafts] = useState({});
  const [reviewReplySavingId, setReviewReplySavingId] = useState("");
  const [reviewReplyDeletingId, setReviewReplyDeletingId] = useState("");
  const [reviewReplyNotice, setReviewReplyNotice] = useState({ type: "", message: "" });

  const productsEndpoint = useMemo(
    () => `/api/products/?page=${paginationModel.page + 1}&limit=${paginationModel.pageSize}`,
    [paginationModel.page, paginationModel.pageSize]
  );
  const hasProductFilters =
    categoryFilter !== "all" ||
    publishedFilter !== "all" ||
    featuredFilter !== "all" ||
    stockFilter !== "all";
  const activeProductFilterCount =
    Number(categoryFilter !== "all") +
    Number(publishedFilter !== "all") +
    Number(featuredFilter !== "all") +
    Number(stockFilter !== "all");
  const isProductFilterOpen = Boolean(productFilterAnchorEl);
  const { data, loading, error } = useGet(productsEndpoint, { keepPreviousData: true });
  const { data: searchableProductsData, loading: searchLoading } = useGet(
    searchQuery || hasProductFilters ? "/api/products/" : null,
    { keepPreviousData: true }
  );
  const { update } = useUpdate("/api/products");
  const { create } = useCreate('/api/products/csv');
  const { create: uploadMedia } = useCreate("/api/media/upload");
  const { remove } = useDelete("/api/products");
  const { mutate } = useSWRConfig();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [categoryFilter, featuredFilter, publishedFilter, searchQuery, stockFilter]);

  // Fetch categories & subcategories
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tags, setTags] = useState([]);
  const { data: categoriesData, loading: categoriesLoading } =
    useGet("/api/categories?all=true");
  const { data: subcategoriesData, loading: subcategoriesLoading } = useGet(
    editProduct?.category
      ? `/api/categories/subcategories/${editProduct.category}?all=true`
      : null
  );
  const { data: tagsData, loading: tagsLoading } = useGet("/api/tag?category=manual");

  // Process products data
  useEffect(() => {
    const sourceData = searchQuery || hasProductFilters ? searchableProductsData : data;

    if (!sourceData) return;
    const productsData = Array.isArray(sourceData)
      ? sourceData
      : sourceData.products || sourceData.data || sourceData.items || [];
    const processedData = productsData.map((product, index) => ({
      ...product,
      heroImage:
        product.heroImage ||
        product.media?.heroImage ||
        product.media?.galleryImages?.[0] ||
        "",
      sku: product.sku || product.SKU || "",
      publishStatus:
        typeof product.publishStatus === "boolean"
          ? product.publishStatus
          : Boolean(product.isPublished),
      id: product._id || `temp-${index}`,
    }));
    if (searchQuery || hasProductFilters) {
      const filteredData = processedData.filter((product) => {
        const isPublished =
          typeof product?.isPublished === "boolean"
            ? product.isPublished
            : Boolean(product?.publishStatus);
        const stock = Number(product?.stock || 0);

        if (!matchesProductSearch(product, searchQuery)) return false;
        if (categoryFilter !== "all" && pickId(product?.category) !== categoryFilter) return false;
        if (publishedFilter !== "all" && isPublished !== (publishedFilter === "published")) return false;
        if (featuredFilter !== "all" && Boolean(product?.isFeatured) !== (featuredFilter === "featured")) return false;
        if (stockFilter === "inStock" && stock <= 0) return false;
        if (stockFilter === "outOfStock" && stock > 0) return false;
        return true;
      });
      const startIndex = paginationModel.page * paginationModel.pageSize;
      const endIndex = startIndex + paginationModel.pageSize;
      setProducts(filteredData.slice(startIndex, endIndex));
      setTotalRows(filteredData.length);
      return;
    }
    setProducts(processedData);
    setTotalRows(Number(sourceData?.total ?? sourceData?.count ?? processedData.length));
  }, [
    categoryFilter,
    data,
    featuredFilter,
    hasProductFilters,
    paginationModel.page,
    paginationModel.pageSize,
    publishedFilter,
    searchQuery,
    searchableProductsData,
    stockFilter,
  ]);

  // Process categories data
  useEffect(() => {
    if (!categoriesData) return;
    if (Array.isArray(categoriesData)) {
      setCategories(categoriesData);
      return;
    }
    if (categoriesData.categories) {
      setCategories(categoriesData.categories);
      return;
    }
    if (categoriesData.data) {
      setCategories(categoriesData.data);
    }
  }, [categoriesData]);

  // Process subcategories data
  useEffect(() => {
    if (!subcategoriesData) {
      setSubcategories([]);
      return;
    }
    if (Array.isArray(subcategoriesData)) {
      setSubcategories(subcategoriesData);
      return;
    }
    if (subcategoriesData.subCategories) {
      setSubcategories(subcategoriesData.subCategories);
      return;
    }
    if (subcategoriesData.subcategories) {
      setSubcategories(subcategoriesData.subcategories);
      return;
    }
    if (subcategoriesData.data) {
      setSubcategories(subcategoriesData.data);
    }
  }, [subcategoriesData]);

  // Process tags data
  useEffect(() => {
    if (!tagsData) {
      setTags([]);
      return;
    }
    if (Array.isArray(tagsData)) {
      setTags(tagsData);
      return;
    }
    if (Array.isArray(tagsData.tags)) {
      setTags(tagsData.tags);
      return;
    }
    if (Array.isArray(tagsData.data)) {
      setTags(tagsData.data);
      return;
    }
    if (Array.isArray(tagsData.items)) {
      setTags(tagsData.items);
      return;
    }
    setTags([]);
  }, [tagsData]);

  const handleViewOpen = useCallback((product) => {
    setViewProduct(product);
    setViewCells(productToCells(product));
    setViewOpen(true);
  }, []);

  const handleViewClose = () => {
    setViewOpen(false);
    setViewProduct(null);
    setViewCells({});
  };

  // Handle edit open
  const handleEditOpen = useCallback((product) => {
    revokeBlobUrl(editHeroPreview);
    revokeBlobUrls(editGalleryPreviews);
    revokeBlobUrl(editVideoPreview);

    const normalizedCategory = pickId(product?.category) || product?.category || "";
    const normalizedSubcategories = normalizeIdList(
      product?.subcategories || product?.subCategories || []
    );
    const normalizedTags = normalizeIdList(product?.tags || product?.tag || []);
    const normalizedPlatinumWeight =
      product?.platinumWeight ??
      product?.metal?.platinum?.weight ??
      "";
    const existingMetal =
      product?.metal && typeof product.metal === "object" ? product.metal : {};
    const normalizedMetalTypes = Array.isArray(existingMetal?.type) ? existingMetal.type : [];
    const galleryImages = resolveGalleryImages(getGalleryImages(product));
    const heroUrl = resolveUploadedMediaUrl(product?.media?.heroImage || product?.heroImage || "");
    const videoUrl = resolveUploadedMediaUrl(product?.media?.productVideo || product?.productVideo || "");
    const nextCells = productToCells(product);
    setEditProduct({
      ...product,
      metaTitle: product?.metaTitle || "",
      metaDescription: product?.metaDescription || "",
      metaKeywords: listToCsv(product?.metaKeywords),
      category: normalizedCategory,
      subcategories: normalizedSubcategories,
      subCategories: normalizedSubcategories,
      tags: normalizedTags,
      tag: normalizedTags,
      platinumWeight: normalizedPlatinumWeight,
      height: product?.height ?? "",
      width: product?.width ?? "",
      metalTypes: normalizedMetalTypes,
      goldPurity: listToCsv(existingMetal?.gold?.purity),
      baseGoldWeight: existingMetal?.gold?.baseGoldWeight ?? "",
      calculatedGoldWeight: existingMetal?.gold?.calculatedGoldWeight ?? "",
      isPublished:
        typeof product?.isPublished === "boolean"
          ? product.isPublished
          : Boolean(product?.publishStatus),
    });
    setEditHeroFile(null);
    setEditGalleryFiles([]);
    setEditVideoFile(null);
    setEditHeroPreview(heroUrl);
    setEditGalleryPreviews(galleryImages);
    setEditVideoPreview(videoUrl);
    setEditCells(nextCells);
    setEditGemFamily("");
    setProductReviews([]);
    setReviewsError("");
    setReviewReplyDrafts({});
    setReviewReplyNotice({ type: "", message: "" });
    setEditOpen(true);
  }, [editGalleryPreviews, editHeroPreview, editVideoPreview]);

  // Handle edit close
  const handleEditClose = () => {
    revokeBlobUrl(editHeroPreview);
    revokeBlobUrls(editGalleryPreviews);
    revokeBlobUrl(editVideoPreview);
    setEditProduct(null);
    setEditCells({});
    setEditGemFamily("");
    setEditHeroFile(null);
    setEditGalleryFiles([]);
    setEditVideoFile(null);
    setEditHeroPreview("");
    setEditGalleryPreviews([]);
    setEditVideoPreview("");
    setProductReviews([]);
    setReviewsLoading(false);
    setReviewsError("");
    setReviewReplyDrafts({});
    setReviewReplySavingId("");
    setReviewReplyDeletingId("");
    setReviewReplyNotice({ type: "", message: "" });
    setEditOpen(false);
  };

  const fetchProductReviews = useCallback(async (productId) => {
    if (!productId) return;
    setReviewsLoading(true);
    setReviewsError("");

    try {
      const response = await apiClient.get(`/api/products/${productId}/reviews`, {
        params: { page: 1, limit: 50 },
      });
      const list = Array.isArray(response?.data?.reviews) ? response.data.reviews : [];
      setProductReviews(list);
      setReviewReplyDrafts(
        list.reduce((acc, review) => {
          acc[review?._id] = String(review?.adminReply?.comment || "");
          return acc;
        }, {})
      );
    } catch (error) {
      setProductReviews([]);
      setReviewsError(error?.response?.data?.message || "Unable to load product reviews.");
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!editOpen) return;
    const productId = editProduct?._id || editProduct?.id;
    if (!productId) return;
    fetchProductReviews(productId);
  }, [editOpen, editProduct?._id, editProduct?.id, fetchProductReviews]);

  const handleReviewReplyDraftChange = (reviewId, value) => {
    setReviewReplyDrafts((prev) => ({
      ...prev,
      [reviewId]: value,
    }));
  };

  const handleSaveReviewReply = async (reviewId) => {
    const productId = editProduct?._id || editProduct?.id;
    const comment = String(reviewReplyDrafts?.[reviewId] || "").trim();
    if (!productId || !reviewId) return;
    if (!comment) {
      setReviewReplyNotice({ type: "error", message: "Reply message cannot be empty." });
      return;
    }

    setReviewReplySavingId(reviewId);
    setReviewReplyNotice({ type: "", message: "" });
    try {
      await apiClient.patch(`/api/products/${productId}/reviews/${reviewId}/reply`, { comment });
      await fetchProductReviews(productId);
      setReviewReplyNotice({ type: "success", message: "Reply saved successfully." });
    } catch (error) {
      setReviewReplyNotice({
        type: "error",
        message: error?.response?.data?.message || "Unable to save reply.",
      });
    } finally {
      setReviewReplySavingId("");
    }
  };

  const handleDeleteReviewReply = async (reviewId) => {
    const productId = editProduct?._id || editProduct?.id;
    if (!productId || !reviewId) return;

    setReviewReplyDeletingId(reviewId);
    setReviewReplyNotice({ type: "", message: "" });
    try {
      await apiClient.delete(`/api/products/${productId}/reviews/${reviewId}/reply`);
      await fetchProductReviews(productId);
      setReviewReplyNotice({ type: "success", message: "Reply deleted successfully." });
    } catch (error) {
      setReviewReplyNotice({
        type: "error",
        message: error?.response?.data?.message || "Unable to delete reply.",
      });
    } finally {
      setReviewReplyDeletingId("");
    }
  };

  const handleRemoveEditHeroImage = () => {
    revokeBlobUrl(editHeroPreview);
    setEditHeroFile(null);
    setEditHeroPreview("");
    setEditProduct((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        heroImage: "",
        media: {
          ...(prev.media && typeof prev.media === "object" ? prev.media : {}),
          heroImage: "",
        },
      };
    });
  };

  const handleRemoveEditGalleryImage = (index) => {
    const targetPreview = editGalleryPreviews[index];
    if (!targetPreview) return;

    const existingGalleryImages = getGalleryImages(editProduct);
    const existingCount = existingGalleryImages.length;

    if (index < existingCount) {
      const nextGalleryImages = existingGalleryImages.filter((_, imageIndex) => imageIndex !== index);
      setEditProduct((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          galleryImages: nextGalleryImages,
          media: {
            ...(prev.media && typeof prev.media === "object" ? prev.media : {}),
            galleryImages: nextGalleryImages,
          },
        };
      });
    } else {
      const newFileIndex = index - existingCount;
      revokeBlobUrl(targetPreview);
      setEditGalleryFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== newFileIndex));
    }

    setEditGalleryPreviews((prev) => prev.filter((_, previewIndex) => previewIndex !== index));
  };

  const handleRemoveEditVideo = () => {
    revokeBlobUrl(editVideoPreview);
    setEditVideoFile(null);
    setEditVideoPreview("");
    setEditProduct((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        productVideo: "",
        media: {
          ...(prev.media && typeof prev.media === "object" ? prev.media : {}),
          productVideo: "",
        },
      };
    });
  };

  // Handle edit save
  const handleEditSave = async () => {
    setSaveLoading(true);
    setSaveError(null);
    try {
      const normalizedSubcategories = normalizeIdList(
        editProduct?.subcategories || editProduct?.subCategories || []
      );
      const normalizedTags = normalizeIdList(editProduct?.tags || editProduct?.tag || []);
      const platinumInput =
        editProduct?.platinumWeight ??
        editProduct?.metal?.platinum?.weight ??
        "";
      const parsedPlatinumWeight = Number(platinumInput);
      const hasPlatinumWeight =
        platinumInput !== "" && Number.isFinite(parsedPlatinumWeight);
      const existingMetal =
        editProduct?.metal && typeof editProduct.metal === "object"
          ? editProduct.metal
          : {};
      const metalTypes = Array.isArray(existingMetal.type)
        ? [...existingMetal.type]
        : [];
      if (hasPlatinumWeight && !metalTypes.includes("Platinum")) {
        metalTypes.push("Platinum");
      }
      const { diamond, gemstones } = cellsToGemPayload(editCells);

      const existingHeroImage =
        editProduct?.media?.heroImage || editProduct?.heroImage || "";
      const existingGalleryImages = getGalleryImages(editProduct);
      const existingProductVideo =
        editProduct?.media?.productVideo || editProduct?.productVideo || "";
      let nextGalleryImages = existingGalleryImages;
      if (Array.isArray(editGalleryFiles) && editGalleryFiles.length > 0) {
        const galleryUploadData = new FormData();
        editGalleryFiles.forEach((file) => galleryUploadData.append("media", file));
        const galleryUploadResponse = await uploadMedia(galleryUploadData, { revalidate: false });
        const uploadedGalleryUrls = normalizeMediaUploadUrls(galleryUploadResponse);
        
        // Only throw if no URLs returned (complete failure)
        if (uploadedGalleryUrls.length === 0) {
          throw new Error("Gallery images could not be uploaded. Please try again.");
        }
        
        // Warn if some images failed, but continue with successfully uploaded ones
        if (uploadedGalleryUrls.length < editGalleryFiles.length) {
          console.warn(
            `Gallery upload incomplete: ${uploadedGalleryUrls.length} of ${editGalleryFiles.length} images uploaded`
          );
        }
        
        nextGalleryImages = [...existingGalleryImages, ...uploadedGalleryUrls];
      }
      const hasMediaFiles = Boolean(editHeroFile) || Boolean(editVideoFile);

      const selectedMetalTypes = Array.isArray(editProduct?.metalTypes)
        ? editProduct.metalTypes.filter(Boolean)
        : [];
      const nextMetalTypes =
        selectedMetalTypes.length > 0 ? [...selectedMetalTypes] : metalTypes;
      if (hasPlatinumWeight && !nextMetalTypes.includes("Platinum")) {
        nextMetalTypes.push("Platinum");
      }
      const parsedBaseGoldWeight = parseNullableNumber(editProduct?.baseGoldWeight, null);
      const parsedCalculatedGoldWeight = parseNullableNumber(
        editProduct?.calculatedGoldWeight,
        null
      );
      const metalPayload = {
        ...(nextMetalTypes.length > 0 ? { type: nextMetalTypes } : {}),
      };
      if (nextMetalTypes.includes("Gold")) {
        metalPayload.gold = {
          purity: csvToList(editProduct?.goldPurity),
          ...(parsedBaseGoldWeight !== null ? { baseGoldWeight: parsedBaseGoldWeight } : {}),
          ...(parsedCalculatedGoldWeight !== null
            ? { calculatedGoldWeight: parsedCalculatedGoldWeight }
            : {}),
        };
      }
      if (nextMetalTypes.includes("Platinum") || hasPlatinumWeight) {
        metalPayload.platinum = {
          ...(hasPlatinumWeight ? { weight: parsedPlatinumWeight } : {}),
        };
      }

      // Prepare the update payload
      const updatePayload = {
        SKU: editProduct.SKU || editProduct.sku || "",
        title: editProduct.title,
        description: editProduct.description || "",
        metaTitle: editProduct.metaTitle || "",
        metaDescription: editProduct.metaDescription || "",
        metaKeywords: csvToList(editProduct.metaKeywords || ""),
        category: pickId(editProduct?.category) || editProduct?.category || null,
        subCategories: normalizedSubcategories,
        subcategories: normalizedSubcategories,
        tag: normalizedTags,
        tags: normalizedTags,
        stock: Number(editProduct.stock || 0),
        height: editProduct?.height || null,
        width: editProduct?.width || null,
        diamond,
        gemstones,
        ...(hasPlatinumWeight ? { platinumWeight: parsedPlatinumWeight } : {}),
        metal: metalPayload,
        isFeatured: Boolean(editProduct.isFeatured),
        isPublished: Boolean(
          typeof editProduct.isPublished === "boolean"
            ? editProduct.isPublished
            : editProduct.publishStatus
        ),
        publishStatus: Boolean(
          typeof editProduct.isPublished === "boolean"
            ? editProduct.isPublished
            : editProduct.publishStatus
        ),
        media: {
          heroImage: existingHeroImage || null,
          galleryImages: nextGalleryImages,
          productVideo: existingProductVideo || "",
        },
      };

      if (hasMediaFiles) {
        const formData = new FormData();
        Object.entries(updatePayload).forEach(([key, value]) => {
          if (typeof value === "undefined") return;
          if (key === "media" && value && typeof value === "object") {
            formData.append("media[heroImage]", value.heroImage || "");
            (Array.isArray(value.galleryImages) ? value.galleryImages : []).forEach((image) => {
              formData.append("media[galleryImages][]", image);
            });
            formData.append("media[productVideo]", value.productVideo || "");
            return;
          }
          if (value === null) {
            formData.append(key, "");
            return;
          }
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
            return;
          }
          formData.append(key, String(value));
        });
        if (editHeroFile) formData.append("heroImage", editHeroFile);
        if (editVideoFile) formData.append("productVideo", editVideoFile);
        await update(editProduct._id || editProduct.id, formData);
      } else {
        await update(editProduct._id || editProduct.id, updatePayload);
      }

      let priceRefreshFailed = false;
      try {
        await triggerPriceUpdateOne(editProduct._id || editProduct.id);
        try {
          await mutate(productsEndpoint);
        } catch {}
        try {
          await mutate('/api/products/');
        } catch {}
      } catch (priceErr) {
        priceRefreshFailed = true;
        // eslint-disable-next-line no-console
        console.warn("Price update-one failed after product update:", priceErr);
      }

      // Update local state
      const updated = products.map((p) =>
        p.id === editProduct.id ? { ...editProduct } : p
      );
      setProducts(updated);

      // Close the dialog
      handleEditClose();

      if (priceRefreshFailed) {
        alert("Product saved, but price refresh failed. Please try again from Main Price.");
      }
    } catch (err) {
      setSaveError(err.response?.data?.message || "Failed to save product");
      console.error("Error saving product:", err);
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await remove(id, { revalidate: false });
      const updated = products.filter((p) => p.id !== id);
      setProducts(updated);
      setTotalRows((prev) => Math.max(0, prev - 1));
      await mutate(productsEndpoint);
      try {
        await mutate('/api/products/');
      } catch {}
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Failed to delete product");
    }
  };

  const handleQuickToggle = async (product, field, nextValue) => {
    const productId = product?._id || product?.id;
    if (!productId) return;

    const currentPublished =
      typeof product?.isPublished === "boolean"
        ? product.isPublished
        : Boolean(product?.publishStatus);
    const toggleKey = `${productId}:${field}`;

    setToggleSavingKey(toggleKey);
    setProducts((prev) =>
      prev.map((item) =>
        item.id === product.id
          ? {
              ...item,
              ...(field === "isFeatured"
                ? { isFeatured: nextValue }
                : { isPublished: nextValue, publishStatus: nextValue }),
            }
          : item
      )
    );

    try {
      await update(
        productId,
        field === "isFeatured"
          ? { isFeatured: nextValue }
          : { isPublished: nextValue, publishStatus: nextValue },
        { revalidate: false }
      );
      await mutate(productsEndpoint);
      try {
        await mutate('/api/products/');
      } catch {}
    } catch (err) {
      setProducts((prev) =>
        prev.map((item) =>
          item.id === product.id
            ? {
                ...item,
                isFeatured: field === "isFeatured" ? Boolean(product?.isFeatured) : item.isFeatured,
                isPublished: currentPublished,
                publishStatus: currentPublished,
              }
            : item
        )
      );
      alert(err?.response?.data?.message || err?.message || "Failed to update product status");
    } finally {
      setToggleSavingKey("");
    }
  };

  const viewGalleryImages = resolveGalleryImages(getGalleryImages(viewProduct));
  const viewHeroImage = resolveUploadedMediaUrl(viewProduct?.media?.heroImage || viewProduct?.heroImage || "");
  const viewProductVideo = resolveUploadedMediaUrl(viewProduct?.media?.productVideo || viewProduct?.productVideo || "");
  const viewCategoryId = pickId(viewProduct?.category) || viewProduct?.category || "";
  const viewSubcategoryIds = normalizeIdList(viewProduct?.subcategories || viewProduct?.subCategories || []);
  const viewTagIds = normalizeIdList(viewProduct?.tags || viewProduct?.tag || []);
  const viewCategoryLabel =
    categories.find((cat) => (cat._id || cat.id) === viewCategoryId)?.name ||
    categories.find((cat) => (cat._id || cat.id) === viewCategoryId)?.categoryName ||
    formatDetailValue(viewProduct?.category);
  const viewSubcategoryLabel = viewSubcategoryIds
    .map((id) => {
      const subcategory = subcategories.find((item) => (item._id || item.id) === id);
      return subcategory?.name || subcategory?.subCategoryName || id;
    })
    .join(", ");
  const viewTagLabel = viewTagIds
    .map((id) => tags.find((item) => (item._id || item.id) === id)?.name || id)
    .join(", ");
  const viewMetal = viewProduct?.metal && typeof viewProduct.metal === "object" ? viewProduct.metal : {};
  const viewDetailSections = [
    {
      title: "Product Details",
      rows: [
        ["SKU", viewProduct?.SKU || viewProduct?.sku],
        ["Title", viewProduct?.title],
        ["Description", viewProduct?.description],
        ["Stock", viewProduct?.stock],
        ["Height", viewProduct?.height],
        ["Width", viewProduct?.width],
        ["Category", viewCategoryLabel],
        ["Subcategories", viewSubcategoryLabel],
        ["Tags", viewTagLabel],
      ],
    },
    {
      title: "Metal Details",
      rows: [
        ["Metal Types", viewMetal?.type],
        ["Gold Purity", viewMetal?.gold?.purity],
        ["Base Gold Weight", viewMetal?.gold?.baseGoldWeight],
        ["Calculated Gold Weight", viewMetal?.gold?.calculatedGoldWeight],
        ["Platinum Weight", viewProduct?.platinumWeight ?? viewMetal?.platinum?.weight],
      ],
    },
    {
      title: "SEO & Status",
      rows: [
        ["Meta Title", viewProduct?.metaTitle],
        ["Meta Description", viewProduct?.metaDescription],
        ["Meta Keywords", viewProduct?.metaKeywords],
        ["Featured", viewProduct?.isFeatured],
        [
          "Published",
          typeof viewProduct?.isPublished === "boolean"
            ? viewProduct.isPublished
            : viewProduct?.publishStatus,
        ],
        ["Created At", viewProduct?.createdAt ? formatReviewDate(viewProduct.createdAt) : ""],
        ["Updated At", viewProduct?.updatedAt ? formatReviewDate(viewProduct.updatedAt) : ""],
      ],
    },
  ];
  const clearProductFilters = () => {
    setCategoryFilter("all");
    setPublishedFilter("all");
    setFeaturedFilter("all");
    setStockFilter("all");
  };
  const isProductsLoading = (searchQuery || hasProductFilters) ? searchLoading : loading;

  // DataGrid columns
  const columns = [
    {
      field: "heroImage",
      headerName: "Image",
      width: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip
          title={
              params.value ? (
              <img
                src={params.value}
                alt={params.row.title}
                style={{ width: 200, height: "auto", borderRadius: 6 }}
              />
            ) : null
          }
        >
          <Box
            component="img"
            src={params.value}
            alt={params.row.title}
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1,
              objectFit: "cover",
              cursor: "pointer",
              border: "1px solid #eaeaea",
            }}
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/64";
            }}
          />
        </Tooltip>
      ),
    },
    {
      field: "sku",
      headerName: "SKU",
      width: 150,
      sortable: true,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 240,
      sortable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>{params.value}</Typography>
          <Typography variant="caption" sx={{ color: '#7b7b7b' }}>{params.row.sku || params.row.SKU || (params.row.id || '').slice(-8)}</Typography>
        </Box>
      ),
    },
    {
      field: "stock",
      headerName: "Stock",
      width: 120,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#169e3a', fontWeight: 700 }}>{(params.row.stock ?? '-') + (params.row.stock ? ' Stock' : '')}</Typography>
      ),
    },
    {
      field: "isFeatured",
      headerName: "Featured",
      width: 110,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const toggleKey = `${params.row._id || params.row.id}:isFeatured`;
        const isSaving = toggleSavingKey === toggleKey;
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            {isSaving ? (
              <CircularProgress size={16} />
            ) : (
              <Switch
                size="small"
                checked={Boolean(params.row.isFeatured)}
                onChange={(event) =>
                  handleQuickToggle(params.row, "isFeatured", event.target.checked)
                }
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "publishStatus",
      headerName: "Published",
      width: 115,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const toggleKey = `${params.row._id || params.row.id}:publishStatus`;
        const isSaving = toggleSavingKey === toggleKey;
        const isPublished =
          typeof params.row?.isPublished === "boolean"
            ? params.row.isPublished
            : Boolean(params.row?.publishStatus);
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            {isSaving ? (
              <CircularProgress size={16} />
            ) : (
              <Switch
                size="small"
                checked={isPublished}
                onChange={(event) =>
                  handleQuickToggle(params.row, "publishStatus", event.target.checked)
                }
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 132,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <Tooltip title="View Product">
            <IconButton
              size="small"
              onClick={() => handleViewOpen(params.row)}
              color="info"
              sx={{ padding: "4px" }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canAccess("products", "edit") && (
            <Tooltip title="Edit Product">
              <IconButton
                size="small"
                onClick={() => handleEditOpen(params.row)}
                color="primary"
                sx={{ padding: "4px" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canAccess("products", "delete") && (
            <Tooltip title="Delete Product">
              <IconButton
                size="small"
                onClick={() => handleDelete(params.row.id)}
                color="error"
                sx={{ padding: "4px" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        padding: { xs: 0, sm: 3 },
        marginTop: { xs: 1, sm: 2 },
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ marginBottom: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1976d2", fontSize: { xs: 22, sm: 24 } }}>
          📦 Product Inventory
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ marginTop: 0.5 }}>
          Manage all your jewelry products - edit details and status
        </Typography>
        <Box
          sx={{
            marginTop: 2,
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          {canAccess("products", "create") && (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/AddProduct')}
                fullWidth={false}
                sx={{ justifyContent: { xs: "center", sm: "flex-start" } }}
              >
                Add Product
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  setBulkOpen(true);
                  setBulkFile(null);
                  setUploadStatus('');
                  setUploadReport(null);
                }}
                sx={{ justifyContent: { xs: "center", sm: "flex-start" } }}
              >
                Add Bulk product
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            size="small"
            href="/templates/product_bulk_template.csv"
            sx={{ justifyContent: { xs: "center", sm: "flex-start" } }}
          >
            Download template
          </Button>
        </Box>
      </Box>

      {error && (
        <Box
          sx={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            padding: 2,
            borderRadius: 1,
            marginBottom: 2,
            border: "1px solid #ef5350",
          }}
        >
          <Typography variant="body2">⚠️ Failed to load products</Typography>
        </Box>
      )}

      <Paper
        elevation={2}
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          backgroundColor: "#fafafa",
          borderRadius: { xs: 0, sm: 2 },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            flexWrap: "wrap",
            p: { xs: 1.5, sm: 2 },
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fff",
          }}
        >
          <TextField
            size="small"
            label="Search products"
            placeholder="Search by SKU, title or description"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 340 } }}
          />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" } }}>
            <Tooltip title="Filter products">
              <IconButton
                color={hasProductFilters ? "primary" : "default"}
                aria-label="Filter products"
                aria-haspopup="true"
                aria-expanded={isProductFilterOpen ? "true" : undefined}
                onClick={(event) => setProductFilterAnchorEl(event.currentTarget)}
                sx={{ border: "1px solid", borderColor: hasProductFilters ? "primary.main" : "divider" }}
              >
                <Badge color="primary" badgeContent={activeProductFilterCount} invisible={!hasProductFilters}>
                  <FilterListIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              {searchQuery || hasProductFilters ? `${totalRows} matching products` : `${totalRows} total products`}
            </Typography>
          </Box>
          <Popover
            open={isProductFilterOpen}
            anchorEl={productFilterAnchorEl}
            onClose={() => setProductFilterAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { mt: 1, p: 2, width: { xs: 300, sm: 360 }, maxWidth: "calc(100vw - 32px)" } } }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Add Filter
              </Typography>
              <FormControl size="small" fullWidth>
                <InputLabel id="product-category-filter-label">Category</InputLabel>
                <Select
                  labelId="product-category-filter-label"
                  label="Category"
                  value={categoryFilter}
                  onChange={(event) => setCategoryFilter(event.target.value)}
                  disabled={categoriesLoading}
                >
                  <MenuItem value="all">All categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat._id || cat.id} value={cat._id || cat.id}>
                      {cat.name || cat.categoryName || "Unnamed"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="product-published-filter-label">Published</InputLabel>
                <Select
                  labelId="product-published-filter-label"
                  label="Published"
                  value={publishedFilter}
                  onChange={(event) => setPublishedFilter(event.target.value)}
                >
                  <MenuItem value="all">All publish statuses</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="unpublished">Unpublished</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="product-featured-filter-label">Featured</InputLabel>
                <Select
                  labelId="product-featured-filter-label"
                  label="Featured"
                  value={featuredFilter}
                  onChange={(event) => setFeaturedFilter(event.target.value)}
                >
                  <MenuItem value="all">All featured statuses</MenuItem>
                  <MenuItem value="featured">Featured</MenuItem>
                  <MenuItem value="notFeatured">Not featured</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="product-stock-filter-label">Stock</InputLabel>
                <Select
                  labelId="product-stock-filter-label"
                  label="Stock"
                  value={stockFilter}
                  onChange={(event) => setStockFilter(event.target.value)}
                >
                  <MenuItem value="all">All stock</MenuItem>
                  <MenuItem value="inStock">In stock</MenuItem>
                  <MenuItem value="outOfStock">Out of stock</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Button size="small" color="inherit" onClick={clearProductFilters} disabled={!hasProductFilters}>
                  Clear filters
                </Button>
                <Button size="small" variant="contained" onClick={() => setProductFilterAnchorEl(null)}>
                  Apply
                </Button>
              </Box>
            </Box>
          </Popover>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", backgroundColor: { xs: "#f6f7f9", sm: "#fff" } }}>
          {isProductsLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 240 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: 90, textAlign: "center" }}>Image</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 120, textAlign: "center" }}>Stock</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 110, textAlign: "center" }}>Featured</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 115, textAlign: "center" }}>Published</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 132, textAlign: "center" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => {
                      const productId = product?._id || product?.id;
                      const featuredToggleKey = `${productId}:isFeatured`;
                      const publishedToggleKey = `${productId}:publishStatus`;
                      const isPublished =
                        typeof product?.isPublished === "boolean"
                          ? product.isPublished
                          : Boolean(product?.publishStatus);

                      return (
                        <TableRow key={product.id} hover sx={{ height: 64 }}>
                          <TableCell align="center">
                            <Tooltip
                              title={
                                product.heroImage ? (
                                  <img
                                    src={product.heroImage}
                                    alt={product.title}
                                    style={{ width: 200, height: "auto", borderRadius: 6 }}
                                  />
                                ) : null
                              }
                            >
                              <Box
                                component="img"
                                src={product.heroImage || "https://via.placeholder.com/64"}
                                alt={product.title}
                                sx={{
                                  width: 64,
                                  height: 64,
                                  borderRadius: 1,
                                  objectFit: "cover",
                                  border: "1px solid #eaeaea",
                                  bgcolor: "#f0f0f0",
                                }}
                                onError={(event) => {
                                  // Only set fallback once to prevent infinite loop
                                  if (event.currentTarget.src !== "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect fill='%23f0f0f0' width='64' height='64'/%3E%3Ctext x='50%25' y='50%25' font-size='12' fill='%23ccc' text-anchor='middle' dominant-baseline='middle'%3ENo Image%3C/text%3E%3C/svg%3E") {
                                    event.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect fill='%23f0f0f0' width='64' height='64'/%3E%3Ctext x='50%25' y='50%25' font-size='12' fill='%23ccc' text-anchor='middle' dominant-baseline='middle'%3ENo Image%3C/text%3E%3C/svg%3E";
                                  }
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>{product.sku || product.SKU || "-"}</TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                              {product.title || "-"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#7b7b7b" }}>
                              {product.sku || product.SKU || (product.id || "").slice(-8)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: "#169e3a", fontWeight: 700 }}>
                              {product.stock ?? "-"}{product.stock ? " Stock" : ""}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {toggleSavingKey === featuredToggleKey ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Switch
                                size="small"
                                checked={Boolean(product.isFeatured)}
                                onChange={(event) =>
                                  handleQuickToggle(product, "isFeatured", event.target.checked)
                                }
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {toggleSavingKey === publishedToggleKey ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Switch
                                size="small"
                                checked={isPublished}
                                onChange={(event) =>
                                  handleQuickToggle(product, "publishStatus", event.target.checked)
                                }
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                              <Tooltip title="View Product">
                                <IconButton size="small" onClick={() => handleViewOpen(product)} color="info" sx={{ padding: "4px" }}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {canAccess("products", "edit") && (
                                <Tooltip title="Edit Product">
                                  <IconButton size="small" onClick={() => handleEditOpen(product)} color="primary" sx={{ padding: "4px" }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canAccess("products", "delete") && (
                                <Tooltip title="Delete Product">
                                  <IconButton size="small" onClick={() => handleDelete(product.id)} color="error" sx={{ padding: "4px" }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          No products found.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </Box>
              <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: 1.25, p: 1.25 }}>
                {products.map((product) => {
                  const productId = product?._id || product?.id;
                  const featuredToggleKey = `${productId}:isFeatured`;
                  const publishedToggleKey = `${productId}:publishStatus`;
                  const isPublished =
                    typeof product?.isPublished === "boolean"
                      ? product.isPublished
                      : Boolean(product?.publishStatus);

                  return (
                    <Box
                      key={product.id}
                      sx={{
                        backgroundColor: "#fff",
                        border: "1px solid #eceff3",
                        borderRadius: 2,
                        boxShadow: "0 10px 24px rgba(16, 24, 40, 0.08)",
                        p: 1.25,
                      }}
                    >
                      <Box sx={{ display: "grid", gridTemplateColumns: "76px minmax(0, 1fr)", gap: 1.25 }}>
                        <Box
                          component="img"
                          src={product.heroImage || "https://via.placeholder.com/96"}
                          alt={product.title}
                          sx={{
                            width: 76,
                            height: 76,
                            borderRadius: 1.5,
                            objectFit: "cover",
                            border: "1px solid #eaeaea",
                            bgcolor: "#f0f0f0",
                          }}
                          onError={(event) => {
                            event.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96'%3E%3Crect fill='%23f0f0f0' width='96' height='96'/%3E%3Ctext x='50%25' y='50%25' font-size='12' fill='%2399a1af' text-anchor='middle' dominant-baseline='middle'%3ENo Image%3C/text%3E%3C/svg%3E";
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 800,
                              lineHeight: 1.25,
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {product.title || "-"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#667085", display: "block", mt: 0.5 }}>
                            SKU: {product.sku || product.SKU || (product.id || "").slice(-8)}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${product.stock ?? "-"}${product.stock ? " Stock" : ""}`}
                            sx={{
                              mt: 1,
                              height: 24,
                              color: "#087a22",
                              backgroundColor: "#edf9f0",
                              fontWeight: 700,
                            }}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1,
                          my: 1.25,
                          pt: 1.25,
                          borderTop: "1px solid #eef0f3",
                        }}
                      >
                        <Box sx={{ border: "1px solid #eef0f3", borderRadius: 1.5, p: 1 }}>
                          <Typography variant="caption" sx={{ color: "#667085", fontWeight: 700 }}>
                            Featured
                          </Typography>
                          <Box sx={{ minHeight: 32, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            {toggleSavingKey === featuredToggleKey ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Switch
                                size="small"
                                checked={Boolean(product.isFeatured)}
                                onChange={(event) =>
                                  handleQuickToggle(product, "isFeatured", event.target.checked)
                                }
                              />
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ border: "1px solid #eef0f3", borderRadius: 1.5, p: 1 }}>
                          <Typography variant="caption" sx={{ color: "#667085", fontWeight: 700 }}>
                            Published
                          </Typography>
                          <Box sx={{ minHeight: 32, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            {toggleSavingKey === publishedToggleKey ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Switch
                                size="small"
                                checked={isPublished}
                                onChange={(event) =>
                                  handleQuickToggle(product, "publishStatus", event.target.checked)
                                }
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
                        <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handleViewOpen(product)}>
                          View
                        </Button>
                        {canAccess("products", "edit") && (
                          <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={() => handleEditOpen(product)}>
                            Edit
                          </Button>
                        )}
                        {canAccess("products", "delete") && (
                          <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => handleDelete(product.id)}>
                            Delete
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {products.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 5, color: "text.secondary", backgroundColor: "#fff", borderRadius: 2 }}>
                    No products found.
                  </Box>
                ) : null}
              </Box>
            </>
          )}
        </Box>
        <TablePagination
          component="div"
          count={totalRows}
          page={paginationModel.page}
          onPageChange={(_, newPage) =>
            setPaginationModel((prev) => ({
              ...prev,
              page: newPage,
            }))
          }
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={(event) =>
            setPaginationModel({
              page: 0,
              pageSize: Number(event.target.value),
            })
          }
          rowsPerPageOptions={[10, 50, 100, 250, 500, 1000]}
          sx={{
            flex: "0 0 auto",
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#f5f5f5",
          }}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            fontWeight: 600,
            padding: "16px",
          }}
        >
          View Product Details
        </DialogTitle>
        <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          {viewProduct ? (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" }, gap: 2, mt: 1 }}>
                <Box
                  component="img"
                  src={viewHeroImage || "https://via.placeholder.com/180"}
                  alt={viewProduct?.title || "Product"}
                  sx={{
                    width: "100%",
                    maxWidth: 180,
                    aspectRatio: "1 / 1",
                    objectFit: "cover",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {viewProduct?.title || "Untitled product"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    SKU: {viewProduct?.SKU || viewProduct?.sku || "-"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      size="small"
                      label={`Stock: ${formatDetailValue(viewProduct?.stock)}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`Featured: ${formatDetailValue(viewProduct?.isFeatured)}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`Published: ${formatDetailValue(typeof viewProduct?.isPublished === "boolean" ? viewProduct.isPublished : viewProduct?.publishStatus)}`}
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Box>

              {viewDetailSections.map((section) => (
                <Box key={section.title}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    {section.title}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {section.rows.map(([label, value]) => (
                          <TableRow key={label}>
                            <TableCell sx={{ width: 220, fontWeight: 600, color: "text.secondary" }}>
                              {label}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                              {formatDetailValue(value)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Media
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {viewGalleryImages.length > 0 ? (
                    viewGalleryImages.map((src, index) => (
                      <Box
                        key={`${src}-${index}`}
                        component="img"
                        src={src}
                        alt={`Gallery ${index + 1}`}
                        sx={{
                          width: 96,
                          height: 96,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No gallery images.
                    </Typography>
                  )}
                </Box>
                {viewProductVideo ? (
                  <Box sx={{ mt: 1.5 }}>
                    <video src={viewProductVideo} controls width="320" style={{ maxWidth: "100%" }} />
                  </Box>
                ) : null}
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Diamond & Gemstones
                </Typography>
                {Object.keys(viewCells).length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Family</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Weight</TableCell>
                          <TableCell>Size</TableCell>
                          <TableCell>Shape</TableCell>
                          <TableCell>Quality</TableCell>
                          <TableCell>Pieces</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(viewCells).flatMap(([type, rows]) =>
                          rows.map((row, index) => (
                            <TableRow key={`${type}-${index}`}>
                              <TableCell>{formatDetailValue(row.family)}</TableCell>
                              <TableCell>{formatDetailValue(row.Type || type)}</TableCell>
                              <TableCell>{formatDetailValue(row.Weight)}</TableCell>
                              <TableCell>{formatDetailValue(row.Size)}</TableCell>
                              <TableCell>{formatDetailValue(row.Shape)}</TableCell>
                              <TableCell>{formatDetailValue(row.Quality)}</TableCell>
                              <TableCell>{formatDetailValue(row.Pieces)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No gemstone details.
                  </Typography>
                )}
              </Box>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Raw Product Data
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: "#f6f8fa",
                      overflow: "auto",
                      fontSize: 12,
                      maxHeight: 360,
                    }}
                  >
                    {JSON.stringify(viewProduct, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <Button onClick={handleViewClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            fontWeight: 600,
            padding: "16px",
          }}
        >
          ✏️ Edit Product
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 4 }}>
          {saveError && (
            <Box
              sx={{
                backgroundColor: "#ffebee",
                color: "#c62828",
                padding: 1.5,
                borderRadius: 1,
                border: "1px solid #ef5350",
              }}
            >
              <Typography variant="body2">⚠️ {saveError}</Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="SKU"
            size="small"
            sx={{ mt: 2.5 }}
            value={editProduct?.SKU || editProduct?.sku || ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, SKU: e.target.value, sku: e.target.value })
            }
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Title"
            size="small"
            value={editProduct?.title || ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, title: e.target.value })
            }
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Description"
            size="small"
            multiline
            rows={3}
            value={editProduct?.description || ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, description: e.target.value })
            }
            variant="outlined"
            sx={{ '& textarea': { resize: 'vertical' } }}
          />
          <TextField
            fullWidth
            label="Meta Keywords"
            size="small"
            multiline
            rows={2}
            value={editProduct?.metaKeywords || ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, metaKeywords: e.target.value })
            }
            placeholder="ruby ring, gold gemstone jewelry"
            helperText="Enter keywords separated by commas."
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Meta Title"
            size="small"
            value={editProduct?.metaTitle || ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, metaTitle: e.target.value })
            }
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Meta Description"
            size="small"
            multiline
            rows={3}
            value={editProduct?.metaDescription || ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, metaDescription: e.target.value })
            }
            variant="outlined"
            sx={{ '& textarea': { resize: 'vertical' } }}
          />
          <TextField
            fullWidth
            label="Stock"
            size="small"
            type="number"
            value={editProduct?.stock ?? ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, stock: e.target.value })
            }
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Height"
            size="small"
            value={editProduct?.height ?? ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, height: e.target.value })
            }
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Width"
            size="small"
            value={editProduct?.width ?? ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, width: e.target.value })
            }
            variant="outlined"
          />
          <Box sx={{ display: "grid", gap: 1 }}>
            <Button variant="outlined" component="label" fullWidth>
              Upload Hero Image
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setEditHeroFile(file);
                  revokeBlobUrl(editHeroPreview);
                  setEditHeroPreview(file ? URL.createObjectURL(file) : "");
                }}
              />
            </Button>
            {editHeroPreview ? (
              <Box
                sx={{
                  position: "relative",
                  width: 160,
                  height: 160,
                }}
              >
                <Box
                  component="img"
                  src={editHeroPreview}
                  alt="Hero preview"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveEditHeroImage}
                  sx={previewActionButtonSx}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ) : null}
          </Box>
          <Box sx={{ display: "grid", gap: 1 }}>
            <Button variant="outlined" component="label" fullWidth>
              Upload Gallery Images
              <input
                hidden
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length === 0) return;

                  setEditGalleryFiles((prev) => [...prev, ...files]);
                  setEditGalleryPreviews((prev) => [
                    ...prev,
                    ...files.map((file) => URL.createObjectURL(file)),
                  ]);
                  e.target.value = "";
                }}
              />
            </Button>
            {editGalleryPreviews.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {editGalleryPreviews.map((src, idx) => (
                  <Box
                    key={`${src}-${idx}`}
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                    }}
                  >
                    <Box
                      component="img"
                      src={src}
                      alt="Gallery preview"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveEditGalleryImage(idx)}
                      sx={previewActionButtonSx}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
          <Box sx={{ display: "grid", gap: 1 }}>
            <Button variant="outlined" component="label" fullWidth>
              Upload Product Video
              <input
                hidden
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setEditVideoFile(file);
                  revokeBlobUrl(editVideoPreview);
                  setEditVideoPreview(file ? URL.createObjectURL(file) : "");
                }}
              />
            </Button>
            {editVideoPreview ? (
              <Box
                sx={{
                  position: "relative",
                  width: 260,
                  display: "inline-block",
                }}
              >
                <video src={editVideoPreview} controls width="260" style={{ borderRadius: 4, border: "1px solid #e0e0e0", display: "block" }} />
                <IconButton
                  size="small"
                  onClick={handleRemoveEditVideo}
                  sx={previewActionButtonSx}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ) : null}
          </Box>
          <TextField
            fullWidth
            label="Gold Purity (comma separated)"
            size="small"
            value={editProduct?.goldPurity || ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, goldPurity: e.target.value })
            }
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Base Gold Weight"
            size="small"
            type="number"
            value={editProduct?.baseGoldWeight ?? ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, baseGoldWeight: e.target.value })
            }
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Calculated Gold Weight"
            size="small"
            type="number"
            value={editProduct?.calculatedGoldWeight ?? ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, calculatedGoldWeight: e.target.value })
            }
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Platinum Weight"
            size="small"
            type="number"
            value={editProduct?.platinumWeight ?? ""}
            onChange={(e) =>
              setEditProduct({ ...editProduct, platinumWeight: e.target.value })
            }
            variant="outlined"
          />
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              padding: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Diamond & Gemstones
            </Typography>
            <DynamicGemstoneCells
              cells={editCells}
              setCells={setEditCells}
              family={editGemFamily}
              setFamily={setEditGemFamily}
            />
          </Box>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={editProduct?.category || ""}
              onChange={(e) =>
                setEditProduct({
                  ...editProduct,
                  category: e.target.value,
                  subcategories: [],
                })
              }
              disabled={categoriesLoading}
            >
              {categoriesLoading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : categories.length === 0 ? (
                <MenuItem disabled>No categories</MenuItem>
              ) : (
                categories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name || cat.categoryName || "Unnamed"}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Subcategories</InputLabel>
            <Select
              multiple
              label="Subcategories"
              value={
                Array.isArray(editProduct?.subcategories)
                  ? editProduct.subcategories
                  : editProduct?.subCategories || []
              }
              onChange={(e) =>
                setEditProduct({
                  ...editProduct,
                  subcategories:
                    typeof e.target.value === "string"
                      ? e.target.value.split(",").filter(Boolean)
                      : e.target.value,
                })
              }
              renderValue={(selected) =>
                (selected || [])
                  .map(
                    (id) =>
                      subcategories.find((s) => s._id === id)?.name ||
                      id
                  )
                  .join(", ")
              }
              disabled={subcategoriesLoading || subcategories.length === 0}
            >
              {subcategoriesLoading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : subcategories.length === 0 ? (
                <MenuItem disabled>No subcategories</MenuItem>
              ) : (
                subcategories.map((sub) => (
                  <MenuItem key={sub._id} value={sub._id}>
                    <Checkbox
                      checked={
                        (editProduct?.subcategories ||
                          editProduct?.subCategories ||
                          []
                        ).indexOf(sub._id) > -1
                      }
                    />
                    <ListItemText
                      primary={
                        sub.name || sub.subCategoryName || "Unnamed"
                      }
                    />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Tags</InputLabel>
            <Select
              multiple
              label="Tags"
              value={
                Array.isArray(editProduct?.tags)
                  ? editProduct.tags
                  : editProduct?.tag || []
              }
              onChange={(e) =>
                setEditProduct({
                  ...editProduct,
                  tags:
                    typeof e.target.value === "string"
                      ? e.target.value.split(",").filter(Boolean)
                      : e.target.value,
                })
              }
              renderValue={(selected) =>
                (selected || [])
                  .map((id) => tags.find((t) => (t._id || t.id) === id)?.name || id)
                  .join(", ")
              }
              disabled={tagsLoading || tags.length === 0}
            >
              {tagsLoading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : tags.length === 0 ? (
                <MenuItem disabled>No tags</MenuItem>
              ) : (
                tags.map((tagItem) => {
                  const id = tagItem._id || tagItem.id;
                  return (
                    <MenuItem key={id} value={id}>
                      <Checkbox
                        checked={(editProduct?.tags || editProduct?.tag || []).indexOf(id) > -1}
                      />
                      <ListItemText primary={tagItem.name || "Unnamed"} />
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              backgroundColor: "#f5f5f5",
              padding: 2,
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                checked={editProduct?.isFeatured || false}
                onChange={(e) =>
                  setEditProduct({
                    ...editProduct,
                    isFeatured: e.target.checked,
                  })
                }
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Featured
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                checked={
                  typeof editProduct?.isPublished === "boolean"
                    ? editProduct.isPublished
                    : editProduct?.publishStatus || false
                }
                onChange={(e) =>
                  setEditProduct({
                    ...editProduct,
                    isPublished: e.target.checked,
                    publishStatus: e.target.checked,
                  })
                }
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Published
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              padding: 2,
              backgroundColor: "#fff",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Review Replies
              </Typography>
              <Chip
                size="small"
                label={`${productReviews.length} review${productReviews.length === 1 ? "" : "s"}`}
                variant="outlined"
              />
            </Box>
            {reviewReplyNotice.message ? (
              <Alert severity={reviewReplyNotice.type === "error" ? "error" : "success"} sx={{ mb: 1.5 }}>
                {reviewReplyNotice.message}
              </Alert>
            ) : null}
            {reviewsLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading reviews...
                </Typography>
              </Box>
            ) : reviewsError ? (
              <Alert severity="error">{reviewsError}</Alert>
            ) : productReviews.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No customer reviews on this product yet.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {productReviews.map((review) => {
                  const reviewId = review?._id || "";
                  const reply = normalizeReviewReply(review?.adminReply);
                  const isSavingReply = reviewReplySavingId === reviewId;
                  const isDeletingReply = reviewReplyDeletingId === reviewId;

                  return (
                    <Box
                      key={reviewId}
                      sx={{
                        border: "1px solid #eceff3",
                        borderRadius: 1.5,
                        padding: 1.5,
                        backgroundColor: "#fafbfc",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {review?.userName || "Verified Buyer"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatReviewDate(review?.createdAt || review?.updatedAt)}
                          </Typography>
                        </Box>
                        <Chip size="small" label={`${Number(review?.rating || 0).toFixed(1)} / 5`} />
                      </Box>
                      {review?.title ? (
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {review.title}
                        </Typography>
                      ) : null}
                      {review?.comment ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, whiteSpace: "pre-wrap" }}>
                          {review.comment}
                        </Typography>
                      ) : null}
                      {reply ? (
                        <Box
                          sx={{
                            mb: 1.5,
                            padding: 1.25,
                            borderRadius: 1,
                            border: "1px solid #e1d7c8",
                            backgroundColor: "#fffaf2",
                          }}
                        >
                          <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 0.5 }}>
                            {reply.adminName} · {formatReviewDate(reply.updatedAt || reply.createdAt)}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {reply.comment}
                          </Typography>
                        </Box>
                      ) : null}
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={3}
                        label={reply ? "Edit admin reply" : "Write admin reply"}
                        value={reviewReplyDrafts?.[reviewId] || ""}
                        onChange={(event) => handleReviewReplyDraftChange(reviewId, event.target.value)}
                      />
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.25 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSaveReviewReply(reviewId)}
                          disabled={isSavingReply || isDeletingReply}
                        >
                          {isSavingReply ? "Saving..." : reply ? "Update Reply" : "Post Reply"}
                        </Button>
                        {reply ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleDeleteReviewReply(reviewId)}
                            disabled={isSavingReply || isDeletingReply}
                          >
                            {isDeletingReply ? "Removing..." : "Delete Reply"}
                          </Button>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "16px", gap: 1 }}>
          <Button onClick={handleEditClose} variant="outlined" disabled={saveLoading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEditSave} 
            color="primary"
            disabled={saveLoading}
          >
            {saveLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
          📥 Bulk Upload Products
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                border: '2px dashed #cfe8ff',
                borderRadius: 2,
                padding: 3,
                textAlign: 'center',
                backgroundColor: '#f6fbff',
                cursor: 'pointer'
              }}
              onClick={() => document.getElementById('bulk-file-input').click()}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: '#1976d2' }} />
              <Typography variant="h6" sx={{ mt: 1 }}>Drag & drop your CSV or Excel file here</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                Accepted: .csv, .xls, .xlsx — Columns should match the template
              </Typography>
              <input
                id="bulk-file-input"
                type="file"
                accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: 'none' }}
                onChange={(e) => {
                  setBulkFile(e.target.files?.[0] || null);
                  setUploadStatus('');
                  setUploadReport(null);
                }}
              />
              <Box sx={{ mt: 1, color: '#333' }}>{bulkFile ? bulkFile.name : 'No file selected'}</Box>
            </Box>

            {uploadStatus && (
              <Alert severity={uploading ? "info" : uploadStatus.toLowerCase().startsWith("upload successful") ? "success" : "error"}>
                {uploadStatus}
              </Alert>
            )}

            {uploadReport && (
              <Box
                sx={{
                  border: "1px solid #e6ecf5",
                  borderRadius: 2,
                  backgroundColor: "#fbfdff",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1a355b" }}>
                  Upload Summary
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                    gap: 1,
                  }}
                >
                  <Chip label={`Parsed: ${uploadReport.parsed}`} color="default" variant="outlined" />
                  <Chip label={`Inserted: ${uploadReport.inserted}`} color="success" variant="outlined" />
                  <Chip label={`Skipped: ${uploadReport.skipped}`} color="warning" variant="outlined" />
                  <Chip label={`Failed: ${uploadReport.failed}`} color={uploadReport.failed > 0 ? "error" : "success"} variant="outlined" />
                </Box>

                {uploadReport.message && (
                  <Typography variant="body2" color="text.secondary">
                    {uploadReport.message}
                  </Typography>
                )}

                {(uploadReport.failedSKUs.length > 0 || uploadReport.missedSKUs.length > 0 || uploadReport.skippedSKUs.length > 0) && <Divider />}

                {uploadReport.failedSKUs.length > 0 && (
                  <Accordion defaultExpanded disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#b3261e" }}>
                        Failed SKUs ({uploadReport.failedSKUs.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                        {uploadReport.failedSKUs.map((sku) => (
                          <Chip key={`failed-${sku}`} label={sku} size="small" color="error" variant="outlined" />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                {uploadReport.missedSKUs.length > 0 && (
                  <Accordion disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#7a4e00" }}>
                        Missed SKUs ({uploadReport.missedSKUs.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                        {uploadReport.missedSKUs.map((sku) => (
                          <Chip key={`missed-${sku}`} label={sku} size="small" color="warning" variant="outlined" />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                {uploadReport.skippedSKUs.length > 0 && (
                  <Accordion disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Skipped SKUs ({uploadReport.skippedSKUs.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                        {uploadReport.skippedSKUs.map((sku) => (
                          <Chip key={`skipped-${sku}`} label={sku} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                {uploadReport.diagnosticInfo.length > 0 && (
                  <Accordion defaultExpanded disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1a355b" }}>
                        Error Details ({uploadReport.diagnosticInfo.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer sx={{ maxHeight: 250, border: "1px solid #ebedf0", borderRadius: 1 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Row</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {uploadReport.diagnosticInfo.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.row}</TableCell>
                                <TableCell>{item.sku}</TableCell>
                                <TableCell>{item.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => setBulkOpen(false)} variant="outlined">Cancel</Button>
              <Button
                variant="contained"
                onClick={async () => {
                  if (!bulkFile) { setUploadStatus('Please select a file first.'); return; }
                  setUploading(true);
                  setUploadStatus('Uploading...');
                  setUploadReport(null);
                  try {
                    const form = new FormData();
                    form.append('file', bulkFile);
                    // useCreate posts via axios; disable revalidate for the csv endpoint and mutate products list
                    const response = await create(form, { revalidate: false });
                    const report = normalizeUploadReport(response);
                    setUploadReport(report);
                    setUploadStatus(
                      `Upload successful. Parsed ${report?.parsed || 0}, inserted ${report?.inserted || 0}, failed ${report?.failed || 0}.`
                    );
                    // refresh products list
                    try { await mutate(productsEndpoint); } catch {}
                    try { await mutate('/api/products/'); } catch {}
                    try {
                      await triggerPriceUpdateAll();
                    } catch (priceErr) {
                      // eslint-disable-next-line no-console
                      console.warn("Price update-all failed after bulk product upload:", priceErr);
                      setUploadStatus((prev) => `${prev} Price refresh failed.`);
                    }
                    // close quickly only when there are no failures to review
                    if (!report || report.failed === 0) {
                      setTimeout(() => { setBulkOpen(false); }, 900);
                    }
                  } catch (err) {
                    setUploadStatus(`Upload failed: ${formatUploadError(err)}`);
                    setUploadReport(normalizeUploadReport(err?.response?.data));
                  } finally {
                    setUploading(false);
                  }
                }}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductList;
